import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { buildWeek, chooseSplit } from '../services/planService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ChevronRight } from 'lucide-react';
import exercisesData from '../data/exercises.json';

function Week() {
  const { user } = useAuth();
  const [week, setWeek] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPlan = async () => {
      if (!user) return;
      try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userPrefs = userDoc.data().prefs || {};
          setPrefs(userPrefs);

          const plan = buildWeek(exercisesData, {
            tier: userPrefs.defaultTier ?? 1,
            minutes: userPrefs.defaultDurationMin ?? 45,
            daysPerWeek: userPrefs.daysPerWeek ?? 3,
            seed: 'wk',
          });
          setWeek(plan);
        }
      } catch (error) {
        console.error('Error loading plan:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPlan();
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!prefs) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-gray-600">No plan configured yet.</p>
          <Link
            to="/"
            className="mt-4 inline-block px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
          >
            Set up your plan
          </Link>
        </div>
      </div>
    );
  }

  const split = chooseSplit(prefs.daysPerWeek, prefs.defaultTier);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">Your Week</h1>
          <p className="mt-1 text-sm text-gray-600">
            {prefs.daysPerWeek} days per week, {prefs.defaultDurationMin} minutes per session
          </p>
        </div>

        <div className="divide-y divide-gray-200">
          {week.map((session, index) => (
            <Link
              key={index}
              to={`/session/${index}/overview`}
              className="block p-6 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-500">
                      Day {index + 1}
                    </span>
                    <span className="text-lg font-bold text-gray-900 capitalize">
                      {split[index]}
                    </span>
                  </div>
                  <div className="mt-2 text-sm text-gray-600">
                    {session.exercises.length} exercises, {session.actualSets} sets
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {session.exercises.slice(0, 3).map((ex) => (
                      <span
                        key={ex.exerciseId}
                        className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                      >
                        {ex.name}
                      </span>
                    ))}
                    {session.exercises.length > 3 && (
                      <span className="px-2 py-1 text-gray-500 text-xs">
                        +{session.exercises.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </Link>
          ))}
        </div>

        <div className="p-6 border-t border-gray-200">
          <Link
            to="/"
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            ← Change settings
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Week;
